import { type ComponentProps, FunctionComponent, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  mediaHelper,
  MultiIntentSearchModal,
  paletteHelper,
  radiusHelper,
  spacingHelper,
} from '@property.com.au/ui-kit';
import { CallToActionBrick } from '../CallToActionBrick/CallToActionBrick';
import propertyPageEstimatedValueAvif from '../../../assets/property-page-estimated-value.avif';
import propertyPageEstimatedValuePng from '../../../assets/property-page-estimated-value.png';
import { useFeatureRollout } from '@rea-argonaut/experiment';
import { getEnv } from '../../../helpers/getEnv';
import { trackSearchRefinementModalOpen } from '../../../helpers/analytics/trackSearchRefinementModalOpen';
import { isHtmlButtonRef } from '../../../helpers/isHtmlButtonRef';

const CTABanner = styled.div`
  display: flex;
  border-radius: ${radiusHelper('extraLarge')};
  margin: ${spacingHelper('0 extraSmall')};
  background: ${paletteHelper('backgroundSecondaryBase')};

  ${mediaHelper('tabletMin')} {
    margin: ${spacingHelper('0 large')};
  }
`;

export const PropertyDataBrick: FunctionComponent = () => {
  const multiIntentSearchModalButtonRef = useRef<HTMLButtonElement>(null);
  const [isMultiIntentSearchModalOpen, setIsMultiIntentSearchModalOpen] = useState<boolean>(false);
  const { isEnabled: isCommuteSearchEnabled } = useFeatureRollout('commute_search');

  const subheading = 'Property Data';
  const title = 'Deep dive on all the properties you’re interested in';
  const subtitle =
    'Check value & rental estimates, detailed property history including photos, government overlays, zoned schools, internet connections and more.';

  const actionItems = [
    {
      id: 'property-data-brick-cta',
      variant: 'outline',
      sizeVariant: 'medium',
      children: 'Search for any Australian property',
      ref: multiIntentSearchModalButtonRef,
      onClick: () => {
        setIsMultiIntentSearchModalOpen(true);
        trackSearchRefinementModalOpen();
      },
    },
  ] satisfies ComponentProps<typeof CallToActionBrick>['actionItems'];

  const image = {
    avifImage: propertyPageEstimatedValueAvif,
    pngImage: propertyPageEstimatedValuePng,
    alt: 'Property Data',
  };

  return (
    <>
      {isHtmlButtonRef(multiIntentSearchModalButtonRef) && (
        <MultiIntentSearchModal
          opened={isMultiIntentSearchModalOpen}
          onClose={() => setIsMultiIntentSearchModalOpen(false)}
          pageSource={'home'}
          buttonRef={multiIntentSearchModalButtonRef}
          rootElementId="argonaut-wrapper"
          isSignedIn={false}
          publicExperienceApiUrl={getEnv.PUBLIC_EXPERIENCE_API_URL()}
          enableCommuteSearch={isCommuteSearchEnabled()}
          googleApiKey={getEnv.GOOGLE_API_KEY()}
        />
      )}
      <CTABanner>
        <CallToActionBrick
          subheading={subheading}
          title={title}
          subtitle={subtitle}
          image={image}
          imagePlacement="right"
          actionItems={actionItems}
        />
      </CTABanner>
    </>
  );
};
